import { useEffect, useState } from "react";
import { Link, useLocation, useParams, Navigate } from "react-router-dom";
import { Package } from "lucide-react";
import { api, inr, fmtDate } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Skel, Empty } from "@/components/common";

export function OrderSuccess() {
  const { id } = useParams();
  const { state } = useLocation();
  const { pct, setSharePuja } = useApp();
  const [o, setO] = useState(null);
  useEffect(() => { api.get(`/orders/${id}`).then((r) => setO(r.data)); }, [id]);
  if (!o) return <div className="container-x py-14"><Skel className="h-80" /></div>;
  const eligible = o.items.filter((i) => i.eligible).reduce((s, i) => s + i.price * i.qty, 0);
  return (
    <div className="container-x max-w-2xl py-14 text-center" data-testid="order-success">
      <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#FEF3C7] text-4xl">🪔</div>
      <h1 className="font-display mt-5 text-4xl font-bold text-[#6B1D2F]">Order Confirmed 🎉</h1>
      <p className="mt-2 text-[#78716C]">Order <b data-testid="order-number">{o.order_no}</b> · Paid {inr(o.payable)}{o.wallet_used > 0 && ` (wallet used ${inr(o.wallet_used)})`}</p>
      <div className="card mt-8 space-y-3 p-5 text-left">{o.items.map((i) => <div key={i.product_id} className="flex items-center gap-3"><img src={i.image} alt="" className="h-12 w-12 rounded-lg object-cover" /><span className="flex-1 text-sm">{i.name} × {i.qty}</span><b className="text-sm">{inr(i.price * i.qty)}</b></div>)}</div>
      <div className="mt-6 rounded-2xl border border-[#E5C378] bg-[#FDF8E2] p-5 text-sm" data-testid="order-referral-note">
        {state?.referral?.applied ? <p>This order was placed through a friend's referral link. They'll receive their reward once your order is delivered. 🙏</p>
          : <p>Referral rewards potential: if this order had been referred, the referrer could earn up to <b>{inr((eligible * pct) / 100)}</b> ({pct}% of eligible items) after delivery. Rewards are credited only when eligibility conditions are met.</p>}
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <button onClick={() => setSharePuja(true)} className="btn-maroon" data-testid="order-share-puja-btn">+ Share your puja after it arrives</button>
        <Link to="/orders" className="btn-ghost" data-testid="view-orders-btn">View my orders</Link>
        <Link to="/refer-and-earn" className="btn-ghost">Refer & Earn</Link>
      </div>
    </div>
  );
}

export function Orders() {
  const { user } = useApp();
  const [list, setList] = useState(null);
  useEffect(() => { if (user) api.get("/orders").then((r) => setList(r.data)); }, [user]);
  if (user === null) return <Navigate to="/login" />;
  return (
    <div className="container-x max-w-3xl py-10">
      <h1 className="font-display mb-6 text-3xl font-bold text-[#6B1D2F]">My Orders</h1>
      {!list ? <Skel className="h-60" /> : !list.length ? <Empty icon="📦" title="No orders yet" action={<Link to="/shop" className="btn-maroon">Start shopping</Link>} /> : (
        <div className="space-y-3">{list.map((o) => (
          <div key={o.id} className="card p-4" data-testid={`order-${o.order_no}`}>
            <div className="flex items-center justify-between"><p className="flex items-center gap-2 font-semibold"><Package className="h-4 w-4 text-[#D97706]" />{o.order_no}</p><span className="rounded-full bg-[#FEF3C7] px-2.5 py-0.5 text-xs font-bold capitalize text-[#B45309]">{o.status.replace(/_/g, " ")}</span></div>
            {o.tracking_no && <p className="mt-1 text-xs text-[#6B1D2F]" data-testid={`order-tracking-${o.order_no}`}>{o.courier} · Tracking {o.tracking_no}</p>}
            <p className="mt-1 text-xs text-[#78716C]">{fmtDate(o.created_at)} · {o.items.length} items · {inr(o.payable)}{o.wallet_used > 0 && ` · wallet ${inr(o.wallet_used)}`}</p>
            <div className="mt-3 flex gap-2">{o.items.map((i) => <img key={i.product_id} src={i.image} alt="" className="h-12 w-12 rounded-lg object-cover" />)}</div>
          </div>
        ))}</div>
      )}
    </div>
  );
}
